import React, { useEffect, useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Card,
  Typography,
  Divider,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import {
  getPresignedUrl,
  previewS3Data,
} from '../../services/content-sources.service';
import { PREVIEW_COLUMNS } from '../../config/module-configs/content-sources.config';
import SnackbarInfo from '../common/snackbar-info.component';
import ViewContentSourceData from '../table-elements/view-content-source-data.component';
import NoData from '../table-elements/no-data.component';
import Error from '../table-elements/error.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const PreviewContentSource = ({ title, dataId, onCancel }) => {
  const columns = PREVIEW_COLUMNS;

  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const totalEpisodesRef = useRef(0);
  const hasError = useRef(false);

  const handleDownloadFile = (fileName) => {
    getPresignedUrl(dataId, { fileName })
      .then((res) => {
        if (res.data) {
          window.open(res.data, '_blank');
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    previewS3Data(dataId)
      .then((res) => {
        Object.keys(res.data).forEach((r) => {
          const seasonEpisodeCnt = Object.keys(res.data[r]).length;
          totalEpisodesRef.current += seasonEpisodeCnt;
        });

        setRowData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        hasError.current = true;
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      onClose={() => onCancel()}
      aria-labelledby="view-dialog-title"
      aria-describedby="view-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="view-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="view-dialog-description">
        {loading && <Loading />}

        {!loading &&
          !hasError.current &&
          Object.keys(rowData).length > 0 &&
          Object.keys(rowData).map((r, index) => {
            const episodeNumbers = Object.keys(rowData[r]);

            return (
              <>
                {index === 0 && (
                  <Box display="flex" alignItems="center" gap={4}>
                    <Box>
                      <Typography variant="subtitle1">
                        Title -{' '}
                        {rowData[r][episodeNumbers[0]]['Movie/Show Title']}
                      </Typography>
                      <Typography variant="subtitle1">
                        Filmhub SKU -{' '}
                        {
                          rowData[r][episodeNumbers[0]][
                            'Movie/Show Filmhub SKU'
                          ]
                        }
                      </Typography>
                    </Box>

                    {rowData[r][episodeNumbers[0]]['Program Type'] ===
                      'Show' && (
                      <Box>
                        <Typography variant="subtitle1">
                          Total Seasons - {Object.keys(rowData).length}
                        </Typography>
                        <Typography variant="subtitle1">
                          Total Episodes - {totalEpisodesRef.current}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                <Divider sx={{ mt: 1.5, mb: 1.5 }} />

                {episodeNumbers.map((e, idx) => (
                  <>
                    {rowData[r][e]['Program Type'] === 'Show' && (
                      <Typography variant="subtitle2" color="secondary" mb={1}>
                        Season {r} - Episode {e}
                      </Typography>
                    )}

                    <Box display="flex" flexDirection="column" gap={2}>
                      <Card>
                        <ViewContentSourceData
                          columns={columns}
                          row={rowData[r][e]}
                          handleDownloadFile={handleDownloadFile}
                        />
                      </Card>
                    </Box>

                    {idx !== episodeNumbers.length - 1 && (
                      <Divider sx={{ mt: 4, mb: 2 }} />
                    )}
                  </>
                ))}
              </>
            );
          })}

        {!loading && !hasError.current && !Object.keys(rowData).length && (
          <Box width="100%" display="flex" justifyContent="center">
            <NoData />
          </Box>
        )}

        {!loading && hasError.current && (
          <Box width="100%" display="flex" justifyContent="center">
            <Error />
          </Box>
        )}
      </DialogContent>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

PreviewContentSource.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PreviewContentSource;
